import { useEffect, useState } from "react";
import {
  KeyRound,
  Server,
  FolderOpen,
  Trash2,
  ShieldCheck,
  ShieldOff,
  Loader2,
  Plus,
  CheckCircle2,
  AlertCircle,
  Info,
  BookOpen
} from "lucide-react";
import deliveryAccessService from "../services/deliveryAccessService";

export default function DeliveryAccess() {
  const [access, setAccess] = useState(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pubkey, setPubkey] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { type, text }

  const refreshKeys = async () => {
    const k = await deliveryAccessService.listKeys();
    if (k.success) setKeys(k.data || []);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    setNotEnabled(false);
    const a = await deliveryAccessService.getAccess();
    if (!a.success) {
      if (/not enabled/i.test(a.message || "")) setNotEnabled(true);
      else setError(a.message || "Failed to load delivery access");
      setLoading(false);
      return;
    }
    setAccess(a.data);
    await refreshKeys();
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pubkey.trim()) return;
    setSubmitting(true);
    setNotice(null);
    const r = await deliveryAccessService.registerKey(pubkey.trim(), label.trim());
    setSubmitting(false);
    if (!r.success) {
      setNotice({ type: "error", text: r.message });
      return;
    }
    setNotice({
      type: "success",
      text: `Key added (${r.data.fingerprint}). It becomes active on the server within about a minute.`
    });
    setPubkey("");
    setLabel("");
    await refreshKeys();
  };

  const revoke = async (fingerprint) => {
    if (
      !window.confirm(
        "Revoke this key? Anyone using it will lose SFTP access on the next sync."
      )
    )
      return;
    const r = await deliveryAccessService.revokeKey(fingerprint);
    if (!r.success) {
      setNotice({ type: "error", text: r.message });
      return;
    }
    setNotice({ type: "success", text: "Key revoked." });
    await refreshKeys();
  };

  // Standard port 22 needs no -P flag; only show it for non-standard ports.
  const portFlag =
    access && access.port && access.port !== 22 ? `-P ${access.port} ` : "";
  const connectCmd = access
    ? `sftp -i ~/.ssh/your_key ${portFlag}${access.username}@${access.host}`
    : "";

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <KeyRound className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Document Delivery (SFTP)</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Register the SSH public keys your team uses to pull your organization's
          delivered documents over SFTP. Keys apply only to your organization's folder.
        </p>

        {notice && (
          <div
            className={`mb-5 flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
              notice.type === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {notice.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            )}
            <span className="break-all">{notice.text}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading…
          </div>
        ) : notEnabled ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <ShieldOff className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <h2 className="text-base font-semibold text-gray-900">
              SFTP delivery isn't enabled for your organization
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Ask your Orbit administrator to enable document delivery for your account.
            </p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
            {error}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Connection details */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Server className="w-4 h-4 text-gray-500" /> Connection
                </h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    access?.enabled
                      ? "bg-green-100 text-green-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {access?.enabled ? (
                    <ShieldCheck className="w-3.5 h-3.5" />
                  ) : (
                    <ShieldOff className="w-3.5 h-3.5" />
                  )}
                  {access?.enabled ? "Active" : "Suspended"}
                </span>
              </div>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-400">Host</dt>
                  <dd className="font-mono text-gray-900">{access?.host}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-400">Port</dt>
                  <dd className="font-mono text-gray-900">{access?.port}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-400">Username</dt>
                  <dd className="font-mono text-gray-900">{access?.username}</dd>
                </div>
                <div className="flex items-start gap-1.5">
                  <FolderOpen className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-400">Folder</dt>
                    <dd className="font-mono text-gray-900 break-all">{access?.folder}</dd>
                  </div>
                </div>
              </dl>
              {connectCmd && (
                <pre className="mt-4 bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto">
                  {connectCmd}
                </pre>
              )}
            </section>

            {/* Add a key */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Add an SSH key</h2>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Public key</label>
                  <textarea
                    value={pubkey}
                    onChange={(e) => setPubkey(e.target.value)}
                    rows={3}
                    placeholder="ssh-ed25519 AAAA… user@host"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                    <Info className="w-3.5 h-3.5" /> Paste a public key (the{" "}
                    <span className="font-mono">.pub</span> file) — never your private key.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-600">Label (optional)</label>
                    <input
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g. Jane's laptop"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !pubkey.trim()}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Register key
                  </button>
                </div>
              </form>
            </section>

            {/* Existing keys */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                Authorized keys ({keys.length})
              </h2>
              {keys.length === 0 ? (
                <div className="text-sm text-gray-400 py-8 text-center">
                  No keys yet — add one above to start pulling documents.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {keys.map((k) => (
                    <li
                      key={k.fingerprint}
                      className="py-3 flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">
                            {k.label || "(unlabeled)"}
                          </span>
                          {k.key_type && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {k.key_type}
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-mono text-gray-500 break-all mt-0.5">
                          {k.fingerprint}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {k.added_by ? `added by ${k.added_by}` : "added"}
                          {k.added_at ? ` · ${k.added_at}` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => revoke(k.fingerprint)}
                        title="Revoke key"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50 text-xs font-medium shrink-0"
                      >
                        <Trash2 className="w-4 h-4" /> Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* How to use — in-app documentation */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gray-500" /> How to use
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Securely pull your organization's underwriting documents over SFTP. Set
                up once, then connect anytime — new documents appear automatically.
              </p>

              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-mono font-bold flex items-center justify-center">1</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-gray-900">Create an SSH key</div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Once per person or machine. This makes a private key (keep it secret)
                      and a <span className="font-mono">.pub</span> public key.
                    </p>
                    <pre className="mt-2 bg-gray-900 text-gray-100 text-xs rounded-lg p-2.5 overflow-x-auto">ssh-keygen -t ed25519 -f ~/.ssh/orbit_sftp</pre>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-mono font-bold flex items-center justify-center">2</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-gray-900">Register the public key</div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Paste the contents of <span className="font-mono">~/.ssh/orbit_sftp.pub</span> into
                      the form above and click <span className="font-medium">Register key</span>. It
                      becomes active on the server within about a minute.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-mono font-bold flex items-center justify-center">3</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-gray-900">Connect and download</div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Use the matching private key. Then <span className="font-mono">ls</span> to browse
                      and <span className="font-mono">get</span> to download.
                    </p>
                    <pre className="mt-2 bg-gray-900 text-gray-100 text-xs rounded-lg p-2.5 overflow-x-auto">{`sftp -i ~/.ssh/orbit_sftp ${portFlag}${access?.username}@${access?.host}`}</pre>
                  </div>
                </li>
              </ol>

              <div className="mt-5 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">What you'll find</h3>
                <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-5">
                  <li>Documents arrive automatically as batches — folders named <span className="font-mono">batch_&lt;timestamp&gt;/</span>.</li>
                  <li>Each batch holds the documents plus an <span className="font-mono">index.txt</span>: an OnBase <b>Tagged DIP</b> manifest for the Document Import Processor.</li>
                  <li>A <span className="font-mono">_COMPLETE</span> marker file is written <b>last</b>. Only import a batch once <span className="font-mono">_COMPLETE</span> is present — that guarantees the batch is fully delivered.</li>
                </ul>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-600">
                <div><b className="text-gray-800">Read-only.</b> You can list and download; you cannot upload or delete.</div>
                <div><b className="text-gray-800">Your folder only.</b> You always land in your organization's folder and never see another tenant's.</div>
                <div><b className="text-gray-800">Multiple keys.</b> Register one per teammate or server; revoke any anytime (applies within ~60s).</div>
                <div><b className="text-gray-800">Public key only.</b> Only paste the <span className="font-mono">.pub</span> file — never your private key.</div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Troubleshooting</h3>
                <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-5">
                  <li><b>"Permission denied (publickey)"</b> — the key isn't registered yet, or the ~60s sync hasn't run. Confirm it's listed above and retry shortly.</li>
                  <li><b>Prompted for a password</b> — same cause: the server accepts registered keys only, and there is no password. Register your key and retry.</li>
                  <li>Connect as user <span className="font-mono">{access?.username}</span> with the private key matching a registered public key.</li>
                </ul>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
